/*
  <uoy-marquee> – a row of cards that scrolls continuously and that visitors can also drag / swipe
  in both directions. Used by sections/uoy-gift-recipients.liquid and sections/uoy-collection-types.liquid.

  Markup:
    <uoy-marquee class="uoy-grm__viewport" data-speed="6" data-pause-hover>
      <div class="uoy-grm__track">  PASS  PASS  PASS  </div>
    </uoy-marquee>
  The track must contain the same list of cards three times (one "pass" = the list once).

  Attributes:
    data-speed        seconds per card (default 6)
    data-pause-hover  pause while the mouse is over the row

  Two modes:
   - autoplay: a Web Animation on the track's transform (runs on the compositor, so it stays smooth
     while the main thread is busy loading theme/app scripts); scrollLeft is 0.
   - manual: animation removed, the element is a native scroller the visitor can drag/swipe;
     scrollLeft is kept inside the middle pass so the loop is endless both ways.
*/
if (!customElements.get('uoy-marquee')) {
  class UoyMarquee extends HTMLElement {
    connectedCallback() {
      if (this._cleanup) return;
      this._init();
    }

    disconnectedCallback() {
      if (this._cleanup) this._cleanup();
      this._cleanup = null;
    }

    _init() {
      const vp = this;
      const track = this.querySelector('.uoy-grm__track');
      if (!track || track.children.length < 3) return;

      const secPerCard = parseFloat(this.dataset.speed) || 6;
      const pauseOnHover = this.hasAttribute('data-pause-hover');
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
      const RESUME_DELAY = 1500;
      const START_DELAY = 2000; // stay still for a moment on page load, then start

      let passW = 0, pxPerSec = 0, anim = null, animStart = 0, resumeTimer = 0;
      let hoverHold = false, focusHold = false, visible = true, drag = null, suppressClick = false;
      const startAt = performance.now() + START_DELAY;

      const measure = () => {
        const cards = track.children;
        const perPass = cards.length / 3;
        passW = cards[perPass].offsetLeft - cards[0].offsetLeft;
        const step = cards.length > 1 ? cards[1].offsetLeft - cards[0].offsetLeft : cards[0].offsetWidth;
        pxPerSec = step / secPerCard;
      };
      const mod = (x) => ((x % passW) + passW) % passW;

      // offset currently on screen, whichever mode we are in
      const currentOffset = () => {
        if (!anim) return vp.scrollLeft;
        const dur = (passW / pxPerSec) * 1000;
        return animStart + (((anim.currentTime || 0) % dur) / dur) * passW;
      };

      const play = () => {
        clearTimeout(resumeTimer);
        const wait = startAt - performance.now();
        if (wait > 0) { resumeTimer = setTimeout(play, wait); return; }
        // re-check the real hover state in case a pointerleave was missed (e.g. after Back navigation)
        hoverHold = pauseOnHover && finePointer && vp.matches(':hover');
        if (anim || reduceMotion || !visible || hoverHold || focusHold || drag || !passW || !pxPerSec) return;
        const x = mod(vp.scrollLeft);
        animStart = x;
        vp.scrollLeft = 0;
        anim = track.animate(
          [{ transform: `translate3d(${-x}px, 0, 0)` }, { transform: `translate3d(${-(x + passW)}px, 0, 0)` }],
          { duration: (passW / pxPerSec) * 1000, iterations: Infinity }
        );
      };

      // stop autoplay and hand over to native scrolling at exactly the same spot
      const freeze = () => {
        clearTimeout(resumeTimer);
        if (!anim) return;
        const x = mod(currentOffset()) + passW;
        anim.cancel();
        anim = null;
        vp.scrollLeft = x;
      };
      const scheduleResume = () => {
        clearTimeout(resumeTimer);
        resumeTimer = setTimeout(play, RESUME_DELAY);
      };

      // manual mode: keep scrollLeft in the middle pass while the visitor scrolls
      vp.addEventListener('scroll', () => {
        if (anim || drag || !passW) return;
        const x = vp.scrollLeft;
        if (x < passW * 0.5) vp.scrollLeft = x + passW;
        else if (x > passW * 1.5) vp.scrollLeft = x - passW;
        scheduleResume();
      }, { passive: true });

      // touch / wheel: switch to manual before the browser starts scrolling
      ['touchstart', 'wheel'].forEach((ev) => vp.addEventListener(ev, () => { freeze(); scheduleResume(); }, { passive: true }));
      vp.addEventListener('touchend', scheduleResume, { passive: true });

      // mouse drag on desktop
      vp.addEventListener('pointerdown', (e) => {
        if (e.pointerType !== 'mouse' || e.button !== 0 || !passW) return;
        freeze();
        drag = { x: e.clientX, start: vp.scrollLeft, moved: false };
        suppressClick = false;
      });
      const onPointerMove = (e) => {
        if (!drag) return;
        const dx = e.clientX - drag.x;
        if (!drag.moved && Math.abs(dx) > 5) {
          drag.moved = true;
          vp.classList.add('is-dragging');
        }
        let x = drag.start - dx;
        if (x < passW * 0.5) { x += passW; drag.start += passW; }
        else if (x > passW * 1.5) { x -= passW; drag.start -= passW; }
        vp.scrollLeft = x;
      };
      const endDrag = () => {
        if (!drag) return;
        suppressClick = drag.moved;
        drag = null;
        vp.classList.remove('is-dragging');
        scheduleResume();
      };
      // a drag must not count as a click on the card under the pointer
      vp.addEventListener('click', (e) => {
        if (suppressClick) { e.preventDefault(); e.stopPropagation(); suppressClick = false; }
      }, true);

      // mouse hover and keyboard focus pause (a mouse click focusing a card must not pause forever)
      if (pauseOnHover) {
        vp.addEventListener('pointerenter', (e) => { if (e.pointerType === 'mouse') { hoverHold = true; freeze(); } });
        vp.addEventListener('pointerleave', (e) => { if (e.pointerType === 'mouse') { hoverHold = false; scheduleResume(); } });
      }
      const isKeyboardFocus = (el) => { try { return el.matches(':focus-visible'); } catch (_) { return false; } };
      vp.addEventListener('focusin', (e) => {
        if (isKeyboardFocus(e.target)) { focusHold = true; freeze(); }
      });
      vp.addEventListener('focusout', () => {
        if (focusHold) { focusHold = false; scheduleResume(); }
      });

      // coming back with the Back button restores the old page state: reset holds and carry on
      const onPageShow = (e) => {
        if (!e.persisted) return;
        focusHold = false;
        drag = null;
        vp.classList.remove('is-dragging');
        scheduleResume();
      };

      window.addEventListener('pointermove', onPointerMove);
      window.addEventListener('pointerup', endDrag);
      window.addEventListener('pointercancel', endDrag);
      window.addEventListener('blur', endDrag);
      window.addEventListener('pageshow', onPageShow);

      // pause while off screen
      let io = null;
      if ('IntersectionObserver' in window) {
        io = new IntersectionObserver(([entry]) => {
          visible = entry.isIntersecting;
          if (anim) visible ? anim.play() : anim.pause();
          else if (visible) play();
        });
        io.observe(vp);
      }

      // (re)measure once layout is ready and whenever card sizes change; keep the current position
      const relayout = () => {
        const prevW = passW;
        const progress = prevW ? mod(currentOffset()) / prevW : 0;
        if (anim) { anim.cancel(); anim = null; }
        measure();
        if (!passW) return;
        vp.scrollLeft = passW + progress * passW;
        play();
      };
      let ro = null;
      if ('ResizeObserver' in window) {
        let lastTrackW = -1;
        ro = new ResizeObserver(() => {
          const w = track.offsetWidth;
          if (w === lastTrackW) return;
          lastTrackW = w;
          relayout();
        });
        ro.observe(track);
      } else {
        relayout();
        window.addEventListener('load', relayout, { once: true });
      }

      this._cleanup = () => {
        clearTimeout(resumeTimer);
        if (anim) anim.cancel();
        if (io) io.disconnect();
        if (ro) ro.disconnect();
        window.removeEventListener('pointermove', onPointerMove);
        window.removeEventListener('pointerup', endDrag);
        window.removeEventListener('pointercancel', endDrag);
        window.removeEventListener('blur', endDrag);
        window.removeEventListener('pageshow', onPageShow);
      };
    }
  }

  customElements.define('uoy-marquee', UoyMarquee);
}
